import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import _ from 'lodash';
import { ReviewPayload, Review } from '../../schema';
import { createReviewAtom, listLoadReviewAtom } from '../../atoms/review';
import { defaultAuth } from '../../atoms/auth';
import {
  notificationAtom,
  NotificationType,
  pageLoadingAtom,
} from '../../atoms/system';
import { useAtom, useSetAtom } from 'jotai';
import produce from 'immer';
import { CommentStatus, Image } from '../../types';
import api, { uploadImages } from '../../api';
import FormReview from './Form.Review';
import { useImmer } from 'use-immer';
import cuid from 'cuid';

const Create = (): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const setListLoad = useSetAtom(listLoadReviewAtom);
  const [showCreateReview, setShowCreateReview] = useAtom(createReviewAtom);
  const form = useForm<ReviewPayload>({
    resolver: zodResolver(Review),
    defaultValues: {
      target_id: '',
      status: CommentStatus.pending,
      amount: 5,
      title: null,
      desc: '',
      youtube: null,
      images: [],
      name: '',
      email: '',
      phone: '',
    },
  });
  const [imageList, setImageList] = useImmer<Image[]>([]);
  const onSubmit = async (data: ReviewPayload) => {
    try {
      pageLoading(true);
      const images = await uploadImages(
        _.toNumber(defaultAuth?.insider_id),
        imageList,
      );

      const payload = produce(data, draft => {
        if (!_.isUndefined(images)) {
          draft.images = images;
        }
      });
      await api.post('api/reviews', {
        json: payload,
      });
      setListLoad(cuid());
      setShowCreateReview(false);
      notification({
        show: true,
        type: NotificationType.success,
        title: 'Tạo mới thành công',
      });
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  return (
    <>
      <Transition show={showCreateReview} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setShowCreateReview(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden p-4 rounded-xl bg-white shadow-xl transition-all">
                  <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <div className="w-full">
                        <h3 className="text-lg font-medium text-gray-900">
                          Tạo đánh giá mới
                        </h3>
                        <FormReview
                          imageProps={{
                            list: imageList,
                            action: setImageList,
                          }}
                        />
                        <div className="flex justify-between w-full mt-6">
                          <button
                            onClick={() => {
                              setShowCreateReview(false);
                            }}
                            type="button"
                            className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          >
                            Hủy
                          </button>
                          <button
                            type="submit"
                            className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Tạo đánh giá
                          </button>
                        </div>
                      </div>
                    </form>
                  </FormProvider>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Create;
